import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import AuthPage from './pages/AuthPage';
import TasksPage from './pages/TasksPage';
import OrdersPage from './pages/OrdersPage';
import HistoryPage from './pages/HistoryPage';
import WalletPage from './pages/WalletPage';
import HelpPage from './pages/HelpPage';
import ChatPage from './pages/ChatPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminOrders from './pages/admin/AdminOrders';
import AdminOrderHistory from './pages/admin/AdminOrderHistory';
import AdminTasks from './pages/admin/AdminTasks';
import AdminCollect from './pages/admin/AdminCollect';
import AdminDeposits from './pages/admin/AdminDeposits';
import AdminWithdrawals from './pages/admin/AdminWithdrawals';
import AdminKyc from './pages/admin/AdminKyc';
import AdminSocials from './pages/admin/AdminSocials';
import AdminUsers from './pages/admin/AdminUsers';
import AdminUserDetail from './pages/admin/AdminUserDetail';
import AdminNewbiePlan from './pages/admin/AdminNewbiePlan';
import AdminChat from './pages/admin/AdminChat';
import AdminSupport from './pages/admin/AdminSupport';
import AdminSettings from './pages/admin/AdminSettings';
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminLayout from './components/AdminLayout';
import BottomNav from './components/BottomNav';
import Toast from './components/Toast';
import SplashScreen from './components/SplashScreen';
import { authService } from './services/authService';
import { clearSession, onSessionExpired } from './services/session';
import { isAdminHost } from './config/api';
import { useTranslation, translateStatic } from './i18n/LocaleContext';

function AppContent() {
  // 两个域名各管各的：会员域名永远渲染 H5，后台域名永远渲染后台面板。
  // 角色判定在 App 里做完，这里不再做跨 origin 的自动跳转。
  if (isAdminHost()) {
    return (
      <Routes>
        <Route path="/" element={<Navigate to="/admin/users" replace />} />
        {/* Chat full screen - không có AdminLayout */}
        <Route path="/admin/chat" element={<AdminChat />} />
        {/* Các route khác có AdminLayout */}
        <Route path="/admin/*" element={
          <AdminLayout>
            <Routes>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="order-history" element={<AdminOrderHistory />} />
              <Route path="tasks" element={<AdminTasks />} />
              <Route path="collect" element={<AdminCollect />} />
              <Route path="deposits" element={<AdminDeposits />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="kyc" element={<AdminKyc />} />
              <Route path="social" element={<AdminSocials />} />
              <Route path="users/:id" element={<AdminUserDetail />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="newbie" element={<AdminNewbiePlan />} />
              <Route path="support" element={<AdminSupport />} />
              <Route path="settings" element={<AdminSettings />} />
            </Routes>
          </AdminLayout>
        } />
        <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
    );
  }

  // User routes
  return (
    <div className="min-h-screen bg-gray-50">
      <Routes>
        <Route path="/" element={<Navigate to="/trang-chu" replace />} />
        <Route path="/trang-chu" element={<TasksPage />} />
        <Route path="/don-hang" element={<OrdersPage />} />
        <Route path="/lich-su" element={<HistoryPage />} />
        <Route path="/vi" element={<WalletPage />} />
        <Route path="/tro-giup" element={<HelpPage />} />
        <Route path="/cskh" element={<ChatPage />} />
      </Routes>
      <BottomNav />
    </div>
  );
}

function App() {
  const { t } = useTranslation();
  const [currentUser, setCurrentUser] = useState(null);
  const [toast, setToast] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [splash, setSplash] = useState({ showing: true, leaving: false });

  useEffect(() => {
    const leave = setTimeout(() => setSplash((s) => ({ ...s, leaving: true })), 2600);
    // 退场 = 蓝圆上涌充满 0.7s + 整体揭幕 0.35s
    const done = setTimeout(() => setSplash((s) => ({ ...s, showing: false })), 3700);
    return () => { clearTimeout(leave); clearTimeout(done); };
  }, []);

  useEffect(() => {
    const user = authService.getCurrentUser();
    const role = user?.userType || user?.role;
    const panelRole = role === 'admin' || role === 'referral_admin';

    // 后台域名只认后台角色；会员账号在这里残留的旧会话直接清掉
    if (user && isAdminHost() && !panelRole) {
      clearSession();
    } else if (user) {
      setCurrentUser(user);
    }

    // 接口 401 说明 token 已失效，回到对应的登录页，而不是留一张空表
    const offSessionExpired = onSessionExpired(() => {
      setCurrentUser(null);
      setSessionExpired(true);
    });

    // Setup axios interceptor để bắt lỗi blocked
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.data?.blocked) {
          // User bị block, đăng xuất và hiển thị thông báo
          setToast({ 
            type: 'error', 
            message: error.response.data.error || translateStatic('accountBlocked')
          });
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setTimeout(() => {
            setCurrentUser(null);
          }, 2000);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      offSessionExpired();
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const handleAuthSuccess = (user) => {
    setSessionExpired(false);
    setCurrentUser(user);
  };

  if (!currentUser) {
    return (
      <>
        {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
        {splash.showing && <SplashScreen leaving={splash.leaving} />}
        {isAdminHost() ? (
          <AdminLoginPage
            onSuccess={handleAuthSuccess}
            notice={sessionExpired ? t('aAuthSessionExpired') : ''}
          />
        ) : (
          <AuthPage onAuthSuccess={handleAuthSuccess} />
        )}
      </>
    );
  }

  return (
    <>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
      {splash.showing && <SplashScreen leaving={splash.leaving} />}
      <Router>
        <AppContent />
      </Router>
    </>
  );
}

export default App;

